import { getAuthToken } from './authUtils';
import { nativeConsole } from '../utils/logger';
import { getTenantIdHeaderValue } from '../utils/tenantScope';

// Lese die API-URL aus Umgebungsvariablen oder nutze einen Fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

/**
 * Zentrale API-Request-Funktion mit automatischer Authentifizierung
 * @param endpoint Der API-Endpoint (relativ zur Basis-URL)
 * @param options Fetch-Optionen
 * @returns Promise mit der Response
 */
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  nativeConsole.log('🔑 API Request:', {
    endpoint,
    baseUrl: API_BASE_URL
  });
  
  // Auth-Token holen
  const token = await getAuthToken();
  
  // Headers zusammenstellen
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  
  // Authorization-Header hinzufügen, wenn Token vorhanden
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    nativeConsole.log('🔐 Added authorization header');
  } else {
    nativeConsole.log('⚠️ No auth token available');
  }
  
  // Content-Type nur setzen, wenn es ein Body gibt
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  // M11 W3: X-Tenant-Id-Header NUR setzen, wenn das Tenant-Scoping-Flag on ist.
  // Bei Flag off liefert getTenantIdHeaderValue() null → kein Header → der PHP-Provider
  // behandelt den fehlenden Header fail-safe als eigenen Tenant (Inertheits-Garantie).
  const tenantId = getTenantIdHeaderValue();
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }
  
  // Vollständige URL zusammenstellen
  const url = `${API_BASE_URL}${endpoint}`;
  
  nativeConsole.log('📤 Sending request:', {
    url,
    method: options.method || 'GET',
    headers: headers
  });
  
  // Request ausführen
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Debug: Response-Info loggen
  nativeConsole.log('📥 Response received:', {
    status: response.status,
    statusText: response.statusText,
    contentType: response.headers.get('content-type'),
    url: response.url,
    allHeaders: Object.fromEntries(response.headers.entries())
  });
  
  // Bei Fehlern: Response-Body loggen
  if (!response.ok) {
    const responseClone = response.clone();
    try {
      const text = await responseClone.text();
      nativeConsole.error('❌ Error response body:', text.substring(0, 200));
    } catch (e) {
      nativeConsole.error('❌ Could not read error response body:', e);
    }
  }
  
  return response;
}; 